import { useState } from 'react'
import {
  formatMoney,
  formatTaxRate,
  type CheckoutSession,
  type PlanCatalogueEntry,
} from '@lb/domain'
import { Trans, useTranslation } from '@lb/i18n'
import { IconLock, Skeleton, cn } from '@lb/ui'

import { Field, FunnelShell, PrimaryButton, inputClass } from './funnel-shell'

/**
 * Confirm — the two-column checkout finalisation screen (approved onboarding
 * HTML §5, `ob-screens.jsx` ObConfirm L282-361).
 *
 * ── The rule that governs the amount side of this screen ───────────────────
 * **The quote is displayed, never computed.** Every number on the right comes
 * from `session.quote`, which the server stored when the member agreed to it.
 * Recomputing at payment time would let a reprice move the total underneath
 * somebody, which is exactly what storing it prevents. There is no arithmetic
 * in this file beyond rendering.
 *
 * ── Plan switching on this screen (approved change) ────────────────────────
 * The approved mock lets a visitor change plan without going back through the
 * whole funnel — the three cards on the left are radio-like and the right
 * side re-quotes on the fly. The current one is highlighted; clicking a
 * different one calls `onSwitchPlan` and the parent restarts the checkout
 * session with the new `planKey`. That is a re-quote server-side too, so the
 * "display, don't compute" rule holds — we never do the math client-side.
 */
export function ConfirmStep({
  session,
  plans,
  busy,
  switching,
  promoBusy,
  promoError,
  onSwitchPlan,
  onApplyPromo,
  onBack,
  onPay,
}: {
  session: CheckoutSession
  plans: PlanCatalogueEntry[]
  busy?: boolean
  /** True while the parent is re-running `start` after `onSwitchPlan`. */
  switching?: boolean
  promoBusy?: boolean
  promoError?: string
  onSwitchPlan: (planKey: string) => void
  onApplyPromo: (code: string | null) => void
  onBack: () => void
  onPay: () => void
}) {
  const { t, i18n } = useTranslation()
  const [code, setCode] = useState(session.promoCode ?? '')
  const [promoOpen, setPromoOpen] = useState(Boolean(session.promoCode))
  const { quote } = session
  const isOrg = session.path === 'ORG'

  const sortedPlans = [...plans].sort((a, b) => a.position - b.position)
  const currentPlanKey = session.planKey
  /*
   * Read the plan's display name off the LOCALISED catalogue, not off the
   * checkout session.
   *
   * `session.planName` is baked in at `POST /api/checkout` time in whatever
   * locale that request used, so a signup started in English and continued
   * on the French confirm screen showed "Monthly" in the FR order summary
   * — reported. `usePlanCatalogue` re-runs when the UI locale changes, so
   * `currentPlan.name` follows the language button. Fall back to the
   * session value if the catalogue has not landed yet (skeleton state).
   */
  const currentPlan = sortedPlans.find((p) => p.key === currentPlanKey)
  const planDisplayName = currentPlan?.name ?? session.planName

  return (
    <FunnelShell
      step={3}
      path={session.path}
      back={{ label: t('onboarding.back'), onClick: onBack }}
      title={t('onboarding.confirmYourPlanTitle')}
      lede={
        isOrg
          ? t('onboarding.confirmOrgLede')
          : t('onboarding.confirmLede')
      }
      width="mid"
    >
      {/*
       * Two columns on md+, stacked on narrow. Left holds the plan switcher,
       * right holds the order summary + CTA — the approved layout.
       */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        {/* ── LEFT: plan switcher ──────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          {sortedPlans.map((plan) => {
            const selected = plan.key === currentPlanKey
            return (
              <button
                key={plan.key}
                type="button"
                aria-pressed={selected}
                disabled={switching || selected}
                onClick={() => onSwitchPlan(plan.key)}
                className={cn(
                  'relative w-full rounded-[var(--r-lg)] border p-4 text-left transition-colors',
                  selected
                    ? 'border-ink bg-surface'
                    : 'border-border bg-surface hover:border-ink-soft',
                  switching && !selected && 'opacity-60',
                )}
              >
                {/*
                 * Both badges share ONE class list so alignment is a
                 * property of the DOM rather than one keeping up with the
                 * other. Reported bug: BEST VALUE and MOST POPULAR did not
                 * line up on the confirm screen — cause was two separate
                 * span declarations drifting.
                 */}
                {plan.badge === 'most-popular' || plan.badge === 'best-value' ? (
                  <span
                    className={cn(
                      'absolute -top-2 left-4 inline-flex h-5 items-center rounded-full px-2 text-[10px] font-semibold uppercase tracking-[0.08em]',
                      plan.badge === 'most-popular'
                        ? 'bg-ink text-white'
                        : 'bg-bleu-gris text-ink-on-tint',
                    )}
                  >
                    {plan.badge === 'most-popular'
                      ? t('onboarding.mostPopular')
                      : t('onboarding.bestValue')}
                  </span>
                ) : null}

                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className={cn(
                      'mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                      selected ? 'border-ink' : 'border-border-deep',
                    )}
                  >
                    {selected ? <span className="h-2 w-2 rounded-full bg-ink" /> : null}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-[15px] font-semibold text-ink">{plan.name}</p>
                      <p className="font-num text-[15px] tabular-nums text-ink">
                        <span className="font-semibold">
                          {money(plan.priceCents, plan.currency, i18n.language)}
                        </span>{' '}
                        <span className="text-[12px] text-muted">
                          {t(`onboarding.period.${plan.period}`)}
                        </span>
                      </p>
                    </div>
                    {plan.subLabel ? (
                      <p className="mt-1 text-[12.5px] text-ink">{plan.subLabel}</p>
                    ) : null}
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* ── RIGHT: order summary + CTA ───────────────────────────────── */}
        <div className="rounded-[var(--r-lg)] border border-border bg-surface p-card">
          <p className="text-[15px] font-semibold text-ink">
            {t('onboarding.orderSummary')}
          </p>

          <dl className="mt-4 flex flex-col gap-2 text-[14px]">
            <Row label={planDisplayName} value={money(quote.subtotalCents, quote.currency, i18n.language)} />

            {session.seats > 1 ? (
              <Row
                label={t('onboarding.seats', { count: session.seats })}
                value=""
                dim
              />
            ) : null}

            {/*
             * Price → discount → tax. Taxing before discounting charges tax
             * on money nobody paid, so the order matches the server's.
             */}
            {quote.discountCents > 0 ? (
              <Row
                label={session.promoCode ? `${t('onboarding.discount')} · ${session.promoCode}` : t('onboarding.discount')}
                value={`− ${money(quote.discountCents, quote.currency, i18n.language)}`}
                tone="positive"
              />
            ) : null}

            {/*
             * Québec taxes, itemised — GST and QST separately, never merged.
             * Labels carry the local shortcut (TPS/TVQ) beside the acronym.
             */}
            {quote.taxes.map((tax) => (
              <Row
                key={tax.code}
                label={`${taxLabel(t, tax.code)} ${formatTaxRate(tax.ratePpm, i18n.language)}`}
                value={money(tax.amountCents, quote.currency, i18n.language)}
              />
            ))}
          </dl>

          {/*
           * Promo code — inside the order summary, where a member looks for
           * it. It used to sit in its own card below the two columns, and UAT
           * scrolled past it and nearly paid without applying the code.
           */}
          <div className="mt-4 border-t border-dashed border-border pt-3">
            {promoOpen ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <Field label={t('onboarding.promo')} error={promoError}>
                    <input
                      className={inputClass}
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder={t('onboarding.optional')}
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  disabled={promoBusy}
                  onClick={() => onApplyPromo(code.trim() ? code.trim() : null)}
                  /* `mb-5` matches `Field`'s trailing margin so the two bottom edges align. */
                  className="mb-5 min-h-[44px] rounded-full border border-border-deep px-4 text-[13px] font-semibold text-ink transition-colors hover:border-ink-soft hover:bg-cream-dark"
                >
                  {promoBusy
                    ? t('onboarding.promoBusy')
                    : session.promoCode
                    ? t('onboarding.promoRemove')
                    : t('onboarding.promoApply')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPromoOpen(true)}
                className="text-[13px] font-semibold text-ink underline underline-offset-2 hover:text-ink-soft"
              >
                {t('onboarding.promoOpen')}
              </button>
            )}
          </div>

          <hr className="my-4 border-border-soft" />

          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[15px] font-semibold text-ink">{t('onboarding.dueToday')}</p>
            <p className="font-num text-[24px] font-semibold tabular-nums text-ink">
              {money(quote.totalCents, quote.currency, i18n.language)}
            </p>
          </div>

          {/*
           * Renewal info box — approved copy. Only shown for subscription paths;
           * the org path invoices out of band and does not renew from here.
           */}
          {!isOrg ? (
            <p className="mt-4 rounded-md bg-cream-dark p-3 text-[12.5px] text-ink-soft">
              {/*
               * The copy has a `<strong>` around the total, so it MUST go
               * through `<Trans>` — `t()` returns the tag as literal text
               * and the notice renders "<strong>19,45 $</strong>" verbatim,
               * which was the "why tags are showing likethis" bug the user
               * reported on both FR and EN. `<Trans>` interpolates variables
               * *and* stitches JSX in through `components`.
               */}
              <Trans
                i18nKey="onboarding.renewsNotice"
                values={{
                  total: money(quote.totalCents, quote.currency, i18n.language),
                  period: t(`onboarding.periodShort.${planPeriod(session, plans)}`),
                }}
                components={{ strong: <strong className="font-semibold text-ink" /> }}
              />
            </p>
          ) : null}

          <div className="mt-6">
            <PrimaryButton busy={busy || switching} onClick={onPay}>
              {/*
               * Lock icon on the CTA per the approved mock — signals the
               * secure handoff.
               */}
              <span className="inline-flex items-center gap-2">
                {!isOrg ? (
                  <IconLock size={14} title={null} />
                ) : null}
                {isOrg ? t('onboarding.invoiceMe') : t('onboarding.continueToSecurePayment')}
              </span>
            </PrimaryButton>
          </div>

          {!isOrg ? (
            <p className="mt-3 flex items-start gap-2 text-[12px] text-muted">
              <IconLock size={12} title={null} className="mt-[3px] shrink-0" />
              <span>{t('onboarding.stripeHandled')}</span>
            </p>
          ) : null}
        </div>
      </div>

    </FunnelShell>
  )
}

function Row({
  label,
  value,
  tone,
  dim,
}: {
  label: string
  value: string
  tone?: 'positive'
  dim?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={cn(dim ? 'text-[12.5px] text-muted' : 'text-muted')}>{label}</dt>
      <dd
        className={cn(
          'font-num tabular-nums',
          tone === 'positive' ? 'text-ink' : 'text-ink-soft',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/**
 * Every amount on this screen, in the language it is being read in.
 *
 * The locale was hardcoded `'fr-CA'` — here and on the two tax rates — so an
 * English signup was quoted « 19,45 $ » on the one screen where the number is
 * the whole point. Same class of defect as the account's hardcoded `locale`,
 * and the landing screen already reads `i18n.language`.
 */
function money(cents: number, currency: string, locale: string): string {
  return formatMoney({ cents, currency }, locale)
}

/** Localise Quebec tax codes ("GST" → "GST (TPS)", "QST" → "QST (TVQ)"). */
function taxLabel(t: (k: string) => string, code: string): string {
  if (code === 'GST') return t('onboarding.taxGst')
  if (code === 'QST') return t('onboarding.taxQst')
  return code
}

/** The period on the current plan, used by the renewal notice. */
function planPeriod(session: CheckoutSession, plans: PlanCatalogueEntry[]): string {
  const current = plans.find((p) => p.key === session.planKey)
  return current?.period ?? 'MONTH'
}

export function ConfirmSkeleton() {
  const { t } = useTranslation()
  return (
    <FunnelShell step={3} title={t('onboarding.confirmYourPlanTitle')} width="mid">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
              <Skeleton className="mb-2 h-5 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
        <div className="rounded-[var(--r-lg)] border border-border bg-surface p-card">
          <Skeleton className="mb-4 h-5 w-40" />
          <Skeleton className="mb-2 h-4 w-full" />
          <Skeleton className="mb-2 h-4 w-3/4" />
          <Skeleton className="h-6 w-32" />
        </div>
      </div>
    </FunnelShell>
  )
}
